import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { FilePlus, Sidebar, XCircle } from '@phosphor-icons/react';
import Add from './Add';
import { UserSavedPrompt } from '@Models/types';
import { useRef, useState } from 'react';
import Modal from '@Common/Modal';
import AddPrompt from '../modals/AddPrompt';
import { usePromptToolboxContext } from '@Context/prompt-toolbox/PromptToolboxContext';
import { BounceLoader } from 'react-spinners';
import colors from '@Common/assets/colors';
import showToast from '@Common/toast/showToast';

interface Props {
  toggled: boolean;
  onToggleSidebar: () => void;
}

const AddPromptView: React.FC<Props> = ({ toggled, onToggleSidebar }) => {
  const { loaders, modals, setView, setModal } = useUtilityContext();
  const [disable, setDisable] = useState(false);
  const { addNewPrompt } = usePromptToolboxContext();

  const addRef = useRef<{
    getValues: () => Omit<UserSavedPrompt, 'id'>;
    clearValues: () => void;
  } | null>(null);

  const handleAddPrompt = async () => {
    if (addRef.current) {
      const values = addRef.current.getValues();
      if (!values.name || values.name.trim() === '') {
        showToast.error('Please provide a name for your prompt');
        return;
      }

      if (
        !values.version[0]?.gpt_result ||
        values.version[0].gpt_result.trim() === ''
      ) {
        showToast.error('Please generate an AI response before saving');
        return;
      }

      setModal('addPrompt', false);
      await addNewPrompt(values);
      addRef.current.clearValues();
    }
  };

  const loadingState = loaders.gptAdd;

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className={`flex flex-row items-center gap-x-4 transition-all duration-300 ease-in-out ${toggled ? 'md:rounded-tr-2xl' : 'md:rounded-t-2xl'} bg-primary-700 px-6 py-4`}
      >
        <button  onClick={onToggleSidebar} >
          <Sidebar size={25} weight="fill" className="text-white" />
        </button>

        <div className="flex w-full flex-row items-center justify-between">
          <span data-testid="new-prompt-header" className="font-inter text-xs font-semibold tracking-widest text-white md:text-base">
            ADD NEW PROMPT
          </span>
          <div className="flex flex-col gap-x-2 gap-y-2 text-sm md:flex-row">
            {loaders.newPrompt ? (
              <div className="flex flex-row items-center gap-x-2">
                <label className="animate-pulse text-sm tracking-wide text-white">
                  Adding New Prompt
                </label>
                <BounceLoader size={28} color={colors.brandWarning[300]} />
              </div>
            ) : (
              <>
                <button data-testid="new-prompt-add-button"
                  disabled={disable || loadingState}
                  className="flex flex-row items-center gap-x-1 rounded-md bg-primary-200 px-3 py-1 disabled:bg-gray-400"
                  onClick={() => {
                    if (addRef.current) {
                      const values = addRef.current.getValues();
                      if (!values.name || values.name.trim() === '') {
                        showToast.error(
                          'Please provide a name for your prompt', "empty-name"
                        );
                        return;
                      }
                      setModal('addPrompt', true);
                    } else {
                      setModal('addPrompt', true);
                    }
                  }}
                >
                  <FilePlus size={20} />
                  <label className="cursor-pointer">Add</label>
                </button>
                <button data-testid="new-prompt-cancel-button"
                  disabled={loadingState}
                  className="flex flex-row items-center gap-x-1 rounded-md bg-brandRed-300 px-3 py-1 text-white disabled:bg-gray-400 disabled:text-black"
                  onClick={() => setView('Diff')}
                >
                  <XCircle size={20} />
                  <label className="cursor-pointer">Cancel</label>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className={`relative flex h-full w-full flex-row gap-x-2 transition-all duration-300 ease-in-out ${toggled ? 'rounded-br-2xl' : 'rounded-b-2xl'} border-2 border-gray-200 p-2`}
      >
        {loaders.newPrompt && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-y-3 rounded-br-2xl rounded-tr-2xl"></div>
        )}
        <Add ref={addRef} setDisable={setDisable} />
      </div>
      <Modal
        title="Add New Prompt"
        isOpen={modals.addPrompt}
        onClose={() => setModal('addPrompt', false)}
      >
        <AddPrompt onAdd={handleAddPrompt} />
      </Modal>
    </div>
  );
};

export default AddPromptView;
